import { action, computed, observable } from 'mobx';

import {
    GenerateOtpRequest,
    IAddUserResponse,
    IGetSessionResponse,
    IPostAuthCodeApiPayload,
    IPostSocialSignUpSessionApiRequest,
    ISavePasswordAndSignInBody,
    ISavePasswordAndSignInResponse,
    IUpdateUserResponse,
    IUserSignInApiPayload,
    IUserSignInResponse,
    IUserState,
    IValidateAddUserResponse,
    OTP_API_PATH,
    OtpResult,
    RampPgApiSdk,
    VerifyOtpRequest
} from '~api/RampPgApiSdk';
import { MessageType, OTP_FLOW } from '~constants/Constants';
import { SignInType } from '~constants/SignInTypeEnum';
import { AccountSignInFormStore } from '~domain/entities/AccountSignInFormStore';
import { GuestSignInFormStore } from '~domain/entities/GuestSignInFormStore';
import { OnlineSignUpFormStore } from '~domain/entities/OnlineSignUpFormStore';
import { OtpStore } from '~domain/entities/OtpStore';
import { SignInWithOtpFormStore } from '~domain/entities/SignInWithOtpFormStore';
import { SocialSignInState } from '~domain/entities/SocialSignInState';
import { TriageAlertState } from '~domain/entities/TriageAlertState';
import { IUserFormValues, UserFormStore } from '~domain/entities/UserFormStore';
import { diContainer } from '~setup/diContainer';
import serviceNames from '~setup/RampService';
import { TaggingEventSourceType } from '~utils/Analytics';

import { ConsentForm } from '../domain/entities/ConsentForm';
import { IMessage } from './MessageRepository';

const otpUrls = {
    [OTP_FLOW.SIGN_IN_WITH_OTP]: '/rest/api/v1/user/sign-in-otp', // TODO: get correct url from BE
    [OTP_FLOW.RESET_PASSWORD]: '/rest/api/v1/user/reset-password-otp', // TODO: get correct url from BE
    [OTP_FLOW.FRAUD]: '/rest/api/v1/user/fraudscore',
    [OTP_FLOW.SIGN_UP]: OTP_API_PATH
};

export class UserRepository {
    // with this flag we can instantiate UserFormStore early without initialValues
    @observable public isUserFormInitialized = false;
    @observable public userFormStore: UserFormStore;
    @observable public accountSignInFormStore: AccountSignInFormStore;
    @observable public guestSignInFormStore: GuestSignInFormStore;
    @observable public onlineSignUpFormStore: OnlineSignUpFormStore;
    // userId of partial sign up created account
    @observable public userId: number = 0;
    // userId in current session
    @observable public signedInUserId?: number;
    @observable public sessionResponse?: IGetSessionResponse;
    // Matched existing user result
    // If two result found, matched by email and mobile respectively, they could possible belongs to one single user.
    // Backend returns both records for fine granular control purpose
    @observable public matchedExistingUsers: IUserState[];
    @observable public triageAlertState: TriageAlertState;
    // Used by in-store journey, cache user profile changes which will used to update user's profile after signing in
    @observable public userProfileChanges?: IUserFormValues;
    private sessionPromise?: Promise<IGetSessionResponse>;
    @observable public isMobileOnlyRegistration: boolean;
    @observable public registeredUserState: IUserState;
    @observable public frontierErrorMessage?: IMessage;
    @observable public posIKalarCallbackUrl?: string;
    @observable public consentForm: ConsentForm;
    @observable public otpStore: OtpStore;
    @observable public signUpEventSource: TaggingEventSourceType = TaggingEventSourceType.ACCOUNT;
    // Sign-in event source for guest checkout scenario is handled separately
    @observable public signInEventSource: TaggingEventSourceType = TaggingEventSourceType.ACCOUNT;
    @observable public signInWithOtpFormStore: SignInWithOtpFormStore;
    @observable public socialSignInState: SocialSignInState;
    @observable public isLoadingState = false;
    private rampPgApiSdk: RampPgApiSdk;

    constructor() {
        this.userFormStore = new UserFormStore();
        this.accountSignInFormStore = new AccountSignInFormStore();
        this.guestSignInFormStore = new GuestSignInFormStore(
            diContainer.get(serviceNames.configRepository)
        );
        this.consentForm = new ConsentForm();
        this.onlineSignUpFormStore = new OnlineSignUpFormStore();
        this.rampPgApiSdk = diContainer.get(serviceNames.rampPgApiSdk);
        this.otpStore = new OtpStore();
        this.signInWithOtpFormStore = new SignInWithOtpFormStore({ otpStore: this.otpStore });
        this.socialSignInState = new SocialSignInState();
        this.triageAlertState = new TriageAlertState();
    }

    @computed
    public get isSignInToConnect() {
        return !!this.consentForm.formValuesBackup;
    }

    @action
    public setIsLoading = (isLoading: boolean) => {
        this.isLoadingState = isLoading;
    };

    @action
    public setSignUpEventSource = (eventSource: TaggingEventSourceType) => {
        this.signUpEventSource = eventSource;
    };

    @action
    public setSignInEventSource = (eventSource: TaggingEventSourceType) => {
        this.signInEventSource = eventSource;
    };

    @action
    public setUserId = (userId?: number | null) => {
        this.userId = userId || 0;
    };

    public setSignedInUserId = (userId: number) => (this.signedInUserId = userId);

    @action
    public setMatchedExistingUsers = (users: IUserState[]) => (this.matchedExistingUsers = users);

    @action
    public setTriageAlertState = (state: TriageAlertState) => (this.triageAlertState = state);

    @action
    public setIsMobileOnlyRegistration = (isMobileOnlyRegistration: boolean) =>
        (this.isMobileOnlyRegistration = isMobileOnlyRegistration);

    @action
    public setRegisteredUser = (userState: IUserState) => {
        this.registeredUserState = userState;
    };

    @action
    public clearPosFrontierError = () => {
        this.frontierErrorMessage = undefined;
    };

    @action
    public setPosFrontierError = (message: string) => {
        this.frontierErrorMessage = {
            transKey: message,
            msgType: MessageType.INFO
        };
    };

    @action
    public setPosIKalarCallbackUrl = (url: string) => (this.posIKalarCallbackUrl = url);

    protected get url() {
        const {
            data: { flow = OTP_FLOW.SIGN_UP }
        } = this.otpStore;

        return otpUrls[flow];
    }

    public validateSignUp = async (values: IUserFormValues): Promise<IValidateAddUserResponse> =>
        this.rampPgApiSdk.validateAddUser(values);

    public signUp = async (
        values: IUserFormValues,
        needValidate?: boolean
    ): Promise<IAddUserResponse> => this.rampPgApiSdk.addUser(values, needValidate);

    public updateUser = async (
        userId: number,
        values: IUserFormValues
    ): Promise<IUpdateUserResponse | Record<string, never>> =>
        this.rampPgApiSdk.updateUser(userId, values);

    public patchPassword = async (userId: number, password: string): Promise<boolean> =>
        this.rampPgApiSdk.patchPassword(userId, password);

    public loadSession = async (): Promise<void> => {
        if (this.sessionPromise) {
            return;
        }

        this.sessionPromise = this.rampPgApiSdk.getSession();
        const sessionResponse = await this.sessionPromise;
        this.sessionResponse = sessionResponse;
        // if user signed-in in two browsers, the user in first browser will be 'timeout' (designed behavior in BE)
        // 'timeout' session has 'userId' in response with 'signedIn' is false
        if (sessionResponse.signedIn && sessionResponse.userId) {
            this.setSignedInUserId(sessionResponse.userId);
        }
    };

    public signIn = async (
        data: IUserSignInApiPayload,
        type?: SignInType
    ): Promise<IUserSignInResponse> => {
        const {
            userId,
            error,
            errorCode,
            riskLevel,
            hmac,
            forwardToCart
        } = await this.rampPgApiSdk.signIn(data, type);

        if (userId) {
            this.setSignedInUserId(userId);
        }

        return {
            userId,
            error,
            errorCode,
            riskLevel,
            hmac,
            forwardToCart
        };
    };

    @action
    public resetSession = async () => {
        const result = await this.rampPgApiSdk.deleteSession();

        if (result) {
            this.sessionPromise = undefined;
            this.signedInUserId = undefined;
            this.sessionResponse = {
                signedIn: false,
                isGuestCheckout: false,
                userId: null
            };
        }

        return result;
    };

    @action initUserFormStore = (initialValues: IUserFormValues = {}) => {
        this.userFormStore.setValues(initialValues);
        this.isUserFormInitialized = true;
    };

    public populateOnlineSignUpFormStore = () => {
        this.onlineSignUpFormStore.setValues({
            password: '',
            ...this.userFormStore.values
        });
    };

    /** Load cached user info from backend cache */
    @action
    public loadSocialProfile = async (provider: string) => {
        return this.rampPgApiSdk.loadSocialProfile(provider);
    };

    @action
    public postAuthCode = async (params: IPostAuthCodeApiPayload) => {
        return this.rampPgApiSdk.postAuthCode(params);
    };

    @action
    public postSocialSignUpSession = async (params: IPostSocialSignUpSessionApiRequest) => {
        return this.rampPgApiSdk.postSocialSignUpSession(params);
    };

    @action
    public disconnectSocial = async (socialProvider: SignInType) => {
        return this.rampPgApiSdk.disconnectSocial(socialProvider);
    };

    @action
    public resetView = () => {
        const {
            setView,
            values: { emailAddress }
        } = this.signInWithOtpFormStore;

        this.accountSignInFormStore.setFieldValue('accountIdentifier', emailAddress);
        setView();
    };

    public generateCode = async (request: GenerateOtpRequest): Promise<boolean> => {
        const result = await this.rampPgApiSdk.otpRequest(
            {
                ...request,
                doRequest: this.rampPgApiSdk.httpTransport.post
            },
            this.url
        );
        if (result) {
            const { success, ...otpStore } = result;
            this.otpStore.setData({ lastTime: new Date().toString(), ...otpStore });

            return success;
        }

        return false;
    };

    public verifyCode = async (request: VerifyOtpRequest): Promise<OtpResult | undefined> =>
        this.rampPgApiSdk.otpRequest(
            {
                ...request,
                doRequest: this.rampPgApiSdk.httpTransport.patch
            },
            this.url
        );

    public savePasswordAndSignIn = async (
        body: ISavePasswordAndSignInBody
    ): Promise<ISavePasswordAndSignInResponse> => this.rampPgApiSdk.savePasswordAndSignIn(body);

    @action
    public cacheUserProfileChanges = () => (this.userProfileChanges = this.userFormStore.values);
}
